import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Card,
  CardContent,
  CardActionArea,
} from '@mui/material';
import AssessmentIcon from '@mui/icons-material/Assessment';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { styled } from '@mui/material/styles';

const StyledCard = styled(Card)(({ theme, selected }) => ({
  minWidth: 200,
  border: selected
    ? `2px solid ${theme.palette.primary.main}`
    : `2px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  transition: 'all 0.3s ease',
  '&:hover': {
    borderColor: theme.palette.primary.main,
    boxShadow: theme.shadows[4],
    transform: 'translateY(-4px)',
  },
  ...(selected && {
    backgroundColor: theme.palette.primary.light + '15',
  }),
}));

const StyledCardContent = styled(CardContent)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: theme.spacing(2),
  padding: theme.spacing(3),
  '&:last-child': {
    paddingBottom: theme.spacing(3),
  },
}));

/**
 * MethodSelectModal Component
 * Modal for selecting risk assessment method when no row is selected
 *
 * @param {Object} props
 * @param {boolean} props.open - Modal open state
 * @param {Function} props.onClose - Close handler
 * @param {Function} props.onConfirm - Confirm handler with selected method
 * @returns {JSX.Element} MethodSelectModal component
 */
const MethodSelectModal = ({ open, onClose, onConfirm }) => {
  const [selectedMethod, setSelectedMethod] = useState(null);

  const methods = [
    {
      value: '위험성 수준 3단계 판단법',
      label: '위험성 수준 3단계 판단법',
      description: '위험성을 3단계로 판단하는 방법',
      icon: <AssessmentIcon sx={{ fontSize: 48, color: 'primary.main' }} />,
    },
    {
      value: '빈도·강도법',
      label: '빈도·강도법',
      description: '위험 가능성과 중대성을 조합한 방법',
      icon: <TrendingUpIcon sx={{ fontSize: 48, color: 'primary.main' }} />,
    },
  ];

  const handleConfirm = () => {
    if (selectedMethod) {
      onConfirm(selectedMethod);
      setSelectedMethod(null);
    }
  };

  const handleClose = () => {
    setSelectedMethod(null);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle
        sx={{
          textAlign: 'center',
          fontSize: '1.5rem',
          fontWeight: 'bold',
          pb: 2,
        }}
      >
        평가 방법 선택
      </DialogTitle>
      <DialogContent>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ textAlign: 'center', mb: 3 }}
        >
          엑셀 다운로드를 위한 평가 방법을 선택해주세요.
        </Typography>
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            justifyContent: 'center',
            flexWrap: 'wrap',
          }}
        >
          {methods.map((method) => (
            <StyledCard
              key={method.value}
              selected={selectedMethod === method.value}
              onClick={() => setSelectedMethod(method.value)}
            >
              <CardActionArea>
                <StyledCardContent>
                  {method.icon}
                  <Typography
                    variant="h6"
                    component="div"
                    sx={{ fontWeight: 'bold', textAlign: 'center' }}
                  >
                    {method.label}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ textAlign: 'center' }}
                  >
                    {method.description}
                  </Typography>
                </StyledCardContent>
              </CardActionArea>
            </StyledCard>
          ))}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} color="inherit">
          취소
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={!selectedMethod}
        >
          확인
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MethodSelectModal;

